
class ScrollClassToggler {
    constructor(triggerElement, targetElement, className, options = {}) {
        this.triggerElement = typeof triggerElement === 'string'
            ? document.querySelector(triggerElement)
            : triggerElement;
        this.targetElement = typeof targetElement === 'string'
            ? document.querySelector(targetElement)
            : targetElement;
        this.className = className;

        // Optionen für verschiedene Lösungsansätze
        this.options = {
            hysteresis: options.hysteresis || 10, // Pixel-Puffer für Hysterese
            useOriginalHeight: options.useOriginalHeight || false, // Originalhöhe verwenden
            debounceTime: options.debounceTime || 0, // Debounce-Zeit in ms
            removeAtTop: options.removeAtTop !== false, // Standardmäßig aktiviert, kann mit false deaktiviert werden
            onClassAdded: options.onClassAdded || null, // Callback wenn Klasse hinzugefügt wird
            onClassRemoved: options.onClassRemoved || null, // Callback wenn Klasse entfernt wird
            ...options
        };

        if (!this.triggerElement || !this.targetElement) {
            console.error('ScrollClassToggler: Trigger- oder Target-Element nicht gefunden');
            return;
        }

        // Status-Tracking
        this.isClassActive = false;
        this.originalTargetHeight = null;
        this.debounceTimer = null;

        this.init();
    }

    init() {
        // Originalhöhe des Target-Elements speichern
        this.storeOriginalHeight();

        this.useScrollListener();
        this.checkPosition();
    }

    storeOriginalHeight() {
        // Speichere die ursprüngliche Höhe bevor die Klasse hinzugefügt wird
        const computedStyle = window.getComputedStyle(this.targetElement);
        this.originalTargetHeight = this.targetElement.offsetHeight;
        this.originalTargetMargin = {
            top: parseInt(computedStyle.marginTop) || 0,
            bottom: parseInt(computedStyle.marginBottom) || 0
        };
    }

    checkPosition() {
        // Prüfe zuerst, ob ganz nach oben gescrollt wurde
        if (this.options.removeAtTop && window.scrollY <= 0) {
            if (this.isClassActive) {
                this.removeClass();
            }
            return; // Beende die weitere Verarbeitung
        }

        const triggerRect = this.triggerElement.getBoundingClientRect();

        // Verwende die ursprüngliche Höhe für die Berechnung wenn gewünscht
        let targetRect;
        if (this.options.useOriginalHeight && this.originalTargetHeight && this.isClassActive) {
            // Simuliere die ursprüngliche Position/Größe
            targetRect = {
                ...this.targetElement.getBoundingClientRect(),
                height: this.originalTargetHeight,
                bottom: this.targetElement.getBoundingClientRect().top + this.originalTargetHeight
            };
        } else {
            targetRect = this.targetElement.getBoundingClientRect();
        }

        // Absolute Positionen berechnen
        const triggerBottom = triggerRect.bottom + window.scrollY;
        const targetBottom = targetRect.bottom + window.scrollY;

        // Hysterese anwenden um "Flackern" zu vermeiden
        const hysteresis = this.options.hysteresis;
        let shouldAddClass;

        if (this.isClassActive) {
            // Wenn Klasse aktiv ist, brauchen wir mehr "Abstand" zum Entfernen
            shouldAddClass = targetBottom > (triggerBottom - hysteresis);
        } else {
            // Wenn Klasse inaktiv ist, brauchen wir mehr "Abstand" zum Hinzufügen
            shouldAddClass = targetBottom > (triggerBottom + hysteresis);
        }

        if (shouldAddClass !== this.isClassActive) {
            if (this.options.debounceTime > 0) {
                this.debouncedToggle(shouldAddClass);
            } else {
                this.toggleClass(shouldAddClass);
            }
        }
    }

    debouncedToggle(shouldAddClass) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            this.toggleClass(shouldAddClass);
        }, this.options.debounceTime);
    }

    toggleClass(shouldAddClass) {
        if (shouldAddClass) {
            this.addClass();
        } else {
            this.removeClass();
        }
    }

    useScrollListener() {
        const handleScroll = () => {
            this.checkPosition();
        };

        let ticking = false;
        const throttledScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    handleScroll();
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener('scroll', throttledScroll, { passive: true });
        window.addEventListener('resize', () => {
            // Bei Resize die ursprüngliche Höhe neu berechnen
            this.storeOriginalHeight();
            throttledScroll();
        }, { passive: true });

        this.scrollListener = throttledScroll;
    }

    addClass() {
        if (!this.isClassActive) {
            this.targetElement.classList.add(this.className);
            this.isClassActive = true;

            // Callback-Funktion ausführen wenn definiert
            if (typeof this.options.onClassAdded === 'function') {
                try {
                    this.options.onClassAdded(this.targetElement, this.className);
                } catch (error) {
                    console.error('ScrollClassToggler: Fehler beim Ausführen des onClassAdded Callbacks:', error);
                }
            }
        }
    }

    removeClass() {
        if (this.isClassActive) {
            this.targetElement.classList.remove(this.className);
            this.isClassActive = false;

            // Callback-Funktion ausführen wenn definiert
            if (typeof this.options.onClassRemoved === 'function') {
                try {
                    this.options.onClassRemoved(this.targetElement, this.className);
                } catch (error) {
                    console.error('ScrollClassToggler: Fehler beim Ausführen des onClassRemoved Callbacks:', error);
                }
            }
        }
    }

    // Öffentliche Methode um Optionen zu aktualisieren
    updateOptions(newOptions) {
        this.options = { ...this.options, ...newOptions };
        this.storeOriginalHeight();
    }

    destroy() {
        if (this.scrollListener) {
            window.removeEventListener('scroll', this.scrollListener);
            window.removeEventListener('resize', this.scrollListener);
        }
        clearTimeout(this.debounceTimer);
    }
}

export default ScrollClassToggler;
